import { t } from '@lingui/core/macro';
import { colorSystem, Flexbox, Text } from '@luminovo/design-system';
import React from 'react';

import { type TooltipRenderProps } from '../types';

function ColorSwatch({ color }: { color: string }) {
    return (
        <span
            style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                borderRadius: 2,
                backgroundColor: color,
                flexShrink: 0,
            }}
        />
    );
}

function SeriesRow({ color, label, value }: { color: string; label: string; value: string }) {
    return (
        <Flexbox gap="16px" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <Flexbox gap="6px" sx={{ alignItems: 'center' }}>
                <ColorSwatch color={color} />
                <Text variant="body-small" color={colorSystem.neutral[7]}>
                    {label}
                </Text>
            </Flexbox>
            <Text
                variant="body-small-semibold"
                color={colorSystem.neutral[8]}
                style={{ fontVariantNumeric: 'tabular-nums' }}
            >
                {value}
            </Text>
        </Flexbox>
    );
}

export function ChartTooltip<TKeys extends string>({
    active,
    label,
    subtitle,
    payload,
    seriesMeta,
    formatValue,
    visibleKeys,
    unstackedKey,
    stacked = true,
}: TooltipRenderProps<TKeys> & {
    /** Shown under the heading, e.g. a supplier number. */
    subtitle?: React.ReactNode;
    visibleKeys: TKeys[];
    /** A series drawn on its own next to the stack. It is listed after the total, and not part of it. */
    unstackedKey?: TKeys;
    /** False when the values don't add up, so there's no total. */
    stacked?: boolean;
}) {
    if (!active || !payload || payload.length === 0) {
        return null;
    }

    const visiblePayload = payload.filter((p) => visibleKeys.includes(p.dataKey) && (p.value ?? 0) !== 0);

    if (visiblePayload.length === 0) {
        return null;
    }

    // Only what is measured in the chart's unit adds up: not the series drawn on its own, not status counts.
    const isMeasured = (key: TKeys) => key !== unstackedKey && !seriesMeta[key]?.status;
    const stackedPayload = visiblePayload.filter((p) => isMeasured(p.dataKey));
    const otherPayload = visiblePayload.filter((p) => !isMeasured(p.dataKey));
    const total = stackedPayload.reduce((sum, entry) => sum + (entry.value ?? 0), 0);

    const renderRow = (entry: (typeof visiblePayload)[number]) => {
        const meta = seriesMeta[entry.dataKey];
        if (!meta) return null;
        const value = meta.formatValue?.(entry.value ?? 0) ?? formatValue(entry.value ?? 0);
        return <SeriesRow key={entry.dataKey} color={meta.color} label={meta.label} value={value} />;
    };

    return (
        <Flexbox gap="4px" style={{ minWidth: 160, padding: '4px 2px' }} sx={{ flexDirection: 'column' }}>
            <Flexbox gap="2px" style={{ marginBottom: 2 }} sx={{ flexDirection: 'column' }}>
                <Text variant="body-small-semibold" color={colorSystem.neutral[8]}>
                    {label}
                </Text>
                {subtitle && (
                    <Text variant="body-small" color={colorSystem.neutral[7]}>
                        {subtitle}
                    </Text>
                )}
            </Flexbox>
            {stackedPayload.map(renderRow)}
            {stacked && stackedPayload.length > 1 && (
                <>
                    <div style={{ borderTop: `1px solid ${colorSystem.neutral[2]}`, margin: '2px 0' }} />
                    <Flexbox gap="16px" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                        <Text variant="body-small" color={colorSystem.neutral[7]}>
                            {t`Total`}
                        </Text>
                        <Text
                            variant="body-small-semibold"
                            color={colorSystem.neutral[8]}
                            style={{ fontVariantNumeric: 'tabular-nums' }}
                        >
                            {formatValue(total)}
                        </Text>
                    </Flexbox>
                </>
            )}
            {otherPayload.length > 0 && stackedPayload.length > 0 && (
                <div style={{ borderTop: `1px solid ${colorSystem.neutral[2]}`, margin: '2px 0' }} />
            )}
            {otherPayload.map(renderRow)}
        </Flexbox>
    );
}
